import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Phone, FileText, Check, ShieldCheck, Clock, ThumbsUp, MapPin, Wrench, Flame, Wind, ArrowRight, Star } from "lucide-react";
import { COMPANY, HERO_POINTS, SERVICES } from "@/lib/siteData";
import CtaBand from "@/components/site/CtaBand";
import Reviews from "@/components/site/Reviews";
import SEO from "@/components/site/SEO";

const SERVICE_ICONS = { plomberie: Wrench, chauffage: Flame, vmc: Wind };

function scrollToAvis(e) {
  if (e) e.preventDefault();
  const el = document.getElementById("avis");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#avis") {
      // Wait for layout before scrolling (fonts / images)
      const t = setTimeout(() => scrollToAvis(), 120);
      return () => clearTimeout(t);
    }
  }, [location]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Plombier Colmar, Mulhouse, Strasbourg | RM Plomberie Chauffage VMC Beblenheim"
        description="Artisan plombier chauffagiste à Colmar, Mulhouse, Strasbourg et tout le Haut-Rhin (68). Installation, dépannage 7j/7, entretien chaudière, VMC, salle de bain. Déplacement et devis gratuits."
        keywords="plombier Colmar, plombier Mulhouse, plombier Strasbourg, chauffagiste Colmar, chauffagiste Mulhouse, plombier Haut-Rhin, plombier 68, plombier Beblenheim, plombier Ribeauvillé, plombier Riquewihr, plombier Kaysersberg, plombier Sélestat, plombier Turckheim, plombier Ingersheim, plombier Wintzenheim, plombier Horbourg-Wihr, plombier Illzach, plombier Rixheim, plombier Schiltigheim, dépannage plomberie 68, chauffage Haut-Rhin, VMC Colmar, entretien chaudière Colmar, installation radiateur Mulhouse, ballon d'eau chaude Colmar, rénovation salle de bain Alsace, pompe à chaleur Haut-Rhin, plancher chauffant Colmar, plombier 24h/24 7j/7 Alsace, urgence plomberie Colmar Mulhouse, artisan plombier Alsace, Ramzi Latrach, RM Plomberie"
      />
      {/* HERO */}
      <section className="relative bg-[#001B35] text-white overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0" aria-hidden="true">
          {/* Brand halos */}
          <div className="absolute -top-32 -left-32 w-[600px] h-[600px] bg-[#005BBB] rounded-full blur-3xl opacity-20" />
          <div className="absolute -bottom-32 right-1/3 w-[520px] h-[520px] bg-[#D90000] rounded-full blur-3xl opacity-12" />
          {/* Subtle pattern */}
          <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-40">
          {/* Logo mobile/tablet — visible above title up to lg */}
          <div className="flex lg:hidden justify-center mb-10 rm-anim" data-testid="hero-logo-mobile">
            <div className="relative">
              <div className="absolute inset-0 bg-[#005BBB] rounded-full blur-3xl opacity-30 scale-110" />
              <div className="absolute inset-0 bg-[#D90000] rounded-full blur-3xl opacity-20 scale-90 translate-x-4 translate-y-4" />
              <img
                src="/logo-rm.jpg"
                alt="Logo RM Plomberie Chauffage VMC"
                className="relative w-44 sm:w-56 h-44 sm:h-56 rounded-3xl object-cover shadow-2xl ring-4 ring-white/15"
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_auto] gap-12 items-center">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.28em] text-[#9bb6d6] mb-5 rm-anim">
                Plomberie • Chauffage • VMC
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.02] max-w-4xl rm-anim rm-delay-1">
                <span className="text-[#5dade2]">RM</span> Plomberie<br />
                <span className="text-white">Chauffage</span> <span className="text-[#D90000]">VMC</span>
              </h1>
              <p className="mt-4 text-lg sm:text-xl font-semibold text-[#5dade2] rm-anim rm-delay-2">
                Plombier chauffagiste à Colmar, Mulhouse, Strasbourg & Haut-Rhin
              </p>
              <p className="mt-5 text-base sm:text-lg text-[#cfdbe8] max-w-2xl rm-anim rm-delay-2">
                Votre artisan plombier chauffagiste basé à Beblenheim intervient rapidement pour tous vos besoins en plomberie, chauffage et ventilation dans tout le Haut-Rhin (68), le Bas-Rhin (67) et sur la Route des Vins d'Alsace.
              </p>

              <a
                href="#avis"
                onClick={scrollToAvis}
                data-testid="hero-trust-badge"
                aria-label="Voir les avis clients — Ils nous font confiance"
                className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/15 hover:border-[#FFB400]/60 pl-3 pr-4 py-2 transition rm-anim rm-delay-2 group"
              >
                <span className="flex" aria-hidden="true">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#FFB400] text-[#FFB400]" />
                  ))}
                </span>
                <span className="text-sm font-semibold text-white tracking-wide">
                  Ils nous font confiance
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-[#9bb6d6] group-hover:text-white group-hover:translate-x-0.5 transition" />
              </a>

              <div className="mt-6 flex flex-wrap gap-3 rm-anim rm-delay-3">
                <a href={`tel:${COMPANY.phoneTel}`} data-testid="hero-cta-phone" className="inline-flex items-center gap-2 bg-[#005BBB] hover:bg-[#0072e8] rounded-full px-7 py-4 font-semibold text-base transition btn-shine">
                  <Phone className="w-5 h-5" /> Appeler maintenant
                </a>
                <Link to="/contact" data-testid="hero-cta-devis" className="inline-flex items-center gap-2 bg-[#D90000] hover:bg-[#b00000] rounded-full px-7 py-4 font-semibold text-base transition btn-shine">
                  <FileText className="w-5 h-5" /> Demander un devis gratuit
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl rm-anim rm-delay-4">
                {HERO_POINTS.map((p) => (
                  <div key={p} className="flex items-start gap-3 border-l-2 border-[#005BBB] pl-3">
                    <Check className="w-5 h-5 text-[#5dade2] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-white">{p}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Big floating logo */}
            <div className="hidden lg:flex justify-center items-center rm-anim rm-delay-2" data-testid="hero-logo-large">
              <div className="relative">
                <div className="absolute inset-0 bg-[#005BBB] rounded-full blur-3xl opacity-30 scale-110" />
                <div className="absolute inset-0 bg-[#D90000] rounded-full blur-3xl opacity-20 scale-90 translate-x-6 translate-y-6" />
                <img
                  src="/logo-rm.jpg"
                  alt="Logo RM Plomberie Chauffage VMC"
                  className="relative w-72 xl:w-80 h-72 xl:h-80 rounded-3xl object-cover shadow-2xl ring-4 ring-white/15"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* SERVICES */}
      <section className="py-20 sm:py-24 bg-white" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Nos services</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#001B35]">
              Des solutions complètes pour votre <span className="text-[#005BBB]">confort</span>
            </h2>
            <div className="mt-5 w-16 h-1 bg-[#005BBB] mx-auto rounded-full" />
          </div>

          <div className="grid md:grid-cols-3 gap-7">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.key];
              return (
                <article
                  key={s.key}
                  data-testid={`service-card-${s.key}`}
                  className="rm-card group bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_-12px_rgba(0,27,53,0.15)] overflow-hidden"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-[#005BBB] flex items-center justify-center shadow-lg">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-display text-xl font-bold text-[#001B35] uppercase tracking-wider">{s.title}</h3>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed">{s.subtitle}</p>
                    <Link to="/nos-services" data-testid={`service-card-link-${s.key}`} className="inline-flex items-center gap-1 mt-5 text-sm font-bold text-[#005BBB] hover:text-[#001B35] uppercase tracking-wider">
                      En savoir plus <ArrowRight className="w-4 h-4 transition group-hover:translate-x-1" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#001B35] text-white dark-grain" data-testid="home-why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold">
              Pourquoi choisir <span className="text-[#5dade2]">RM Plomberie</span> ?
            </h2>
            <div className="mt-4 w-16 h-1 bg-[#D90000] mx-auto rounded-full" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { Icon: ShieldCheck, title: "Expertise & qualité", text: "Un travail soigné réalisé par un professionnel qualifié." },
              { Icon: Clock, title: "Réactivité", text: "Intervention rapide pour tous vos dépannages." },
              { Icon: ThumbsUp, title: "Satisfaction client", text: "Des clients satisfaits et des avis positifs." },
              { Icon: FileText, title: "Devis gratuit", text: "Des conseils personnalisés et des devis transparents." },
            ].map(({ Icon, title, text }) => (
              <div key={title} className="text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl border border-white/15 bg-white/5 flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-[#5dade2]" />
                </div>
                <h3 className="font-display font-bold uppercase tracking-wider text-sm">{title}</h3>
                <p className="mt-2 text-sm text-[#9bb6d6] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ZONES — SEO local massif */}
      <section className="py-20 bg-[#F5F7FA]" data-testid="home-zones">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Zone d'intervention</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#001B35]">
                Plombier chauffagiste à <span className="text-[#005BBB]">Colmar, Mulhouse, Strasbourg</span> et tout le Haut-Rhin
              </h2>
              <p className="mt-5 text-gray-600 leading-relaxed max-w-xl">
                Basée à Beblenheim (68980), l'entreprise <strong>RM Plomberie Chauffage VMC</strong> intervient dans tout le Haut-Rhin (68) et le Bas-Rhin (67) pour l'installation, le dépannage et l'entretien de vos équipements en plomberie, chauffage et VMC. <strong>Déplacement gratuit</strong> dans toute notre zone.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Beblenheim", "Colmar", "Mulhouse", "Strasbourg", "Ribeauvillé", "Riquewihr", "Kaysersberg", "Sélestat", "Turckheim", "Wintzenheim", "Ammerschwihr", "Bennwihr", "Mittelwihr", "Hunawihr"].map((v) => (
                  <span key={v} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-[#001B35]">
                    <MapPin className="w-3.5 h-3.5 text-[#005BBB]" /> {v}
                  </span>
                ))}
              </div>
              <div className="mt-8">
                <a href={`tel:${COMPANY.phoneTel}`} data-testid="zones-cta" className="inline-flex items-center gap-2 bg-[#001B35] hover:bg-[#005BBB] text-white rounded-full px-6 py-3 font-semibold transition">
                  <Phone className="w-4 h-4" /> {COMPANY.phone}
                </a>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-200 aspect-[5/4]">
              <iframe
                title="Carte zone d'intervention RM Plomberie Colmar Mulhouse Strasbourg"
                src={`https://www.google.com/maps?q=${COMPANY.mapsQuery}&z=10&output=embed`}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Grid détaillée par ville — booster SEO local */}
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                city: "Plombier à Colmar",
                text: "Intervention rapide sur Colmar et sa périphérie pour tous vos dépannages plomberie, installations sanitaires, rénovation salle de bain, débouchage canalisation et recherche de fuite.",
                towns: "Colmar, Horbourg-Wihr, Ingersheim, Wintzenheim, Turckheim, Houssen, Wettolsheim",
              },
              {
                city: "Chauffagiste à Mulhouse",
                text: "Installation et entretien chaudière gaz, remplacement de radiateurs, mise en place de pompes à chaleur et plancher chauffant à Mulhouse et alentours.",
                towns: "Mulhouse, Illzach, Rixheim, Kingersheim, Wittenheim, Riedisheim, Brunstatt",
              },
              {
                city: "Plombier à Strasbourg",
                text: "Prestations plomberie et chauffage à Strasbourg et couronne : dépannage 7j/7, entretien annuel chaudière, installation VMC double flux, ballon d'eau chaude.",
                towns: "Strasbourg, Schiltigheim, Illkirch, Bischheim, Lingolsheim, Ostwald, Hoenheim",
              },
              {
                city: "Route des Vins d'Alsace",
                text: "Artisan plombier chauffagiste local pour les particuliers et professionnels de la Route des Vins : rénovation de maisons alsaciennes, chauffage traditionnel et moderne.",
                towns: "Ribeauvillé, Riquewihr, Kaysersberg, Bergheim, Hunawihr, Mittelwihr, Bennwihr, Sigolsheim",
              },
              {
                city: "Sélestat et environs",
                text: "Zone Sélestat / Marckolsheim / Val d'Argent : installation complète plomberie neuf et rénovation, entretien chaudière, VMC hygroréglable et double flux.",
                towns: "Sélestat, Marckolsheim, Sainte-Marie-aux-Mines, Villé, Châtenois, Ebersheim",
              },
              {
                city: "Haut-Rhin (68) — Bas-Rhin (67)",
                text: "Nous couvrons l'ensemble du département 68 et une grande partie du 67. Devis gratuit sans engagement pour tout projet en Alsace.",
                towns: "Guebwiller, Thann, Saint-Louis, Altkirch, Cernay, Ensisheim, Rouffach",
              },
            ].map((z) => (
              <article key={z.city} className="rm-card bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_4px_20px_-12px_rgba(0,27,53,0.12)]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#005BBB]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#005BBB]" />
                  </div>
                  <h3 className="font-display font-bold text-[#001B35] text-lg leading-tight">{z.city}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{z.text}</p>
                <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                  <span className="font-semibold text-[#001B35]">Communes :</span> {z.towns}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />

      <Reviews />
    </div>
  );
}
